import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import Loading from "../../components/Loading";
import TournamentBracket from "../../components/VirtualWorld/TournamentBracket";
import SponsorMark from "../../components/VirtualWorld/SponsorMark";
import { formatManagerDate, relativeUntil } from "../../helper/managerDate";

const TABS = [
  { id: "regular", label: "Reguläre Liga (BO5)", emoji: "🎯" },
  { id: "flowliga", label: "Team-FlowLiga", emoji: "📉" },
  { id: "tournament", label: "Team-Turniere", emoji: "🏅" },
];

const money = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;
const dt = (value) => (value ? formatManagerDate(value) : "—");

function TableHead({ tab }) {
  return (
    <thead className="bg-gray-200 dark:bg-gray-800">
      <tr>
        <th className="p-2">#</th>
        <th className="p-2">Club</th>
        <th className="p-2">Sp</th>
        <th className="p-2">S</th>
        <th className="p-2">U</th>
        <th className="p-2">N</th>
        {tab === "regular" ? <th className="p-2">Spiele</th> : null}
        {tab === "regular" ? <th className="p-2">Diff</th> : null}
        {tab === "flowliga" ? <th className="p-2">Rest</th> : null}
        {tab === "flowliga" ? <th className="p-2">Gegner-Rest</th> : null}
        <th className="p-2">Pkt</th>
      </tr>
    </thead>
  );
}

function TableRow({ row, i, tab, ownId }) {
  const label = row.displayName || row.name;
  return (
    <tr className={`border-t border-gray-200 dark:border-gray-600 ${row.id === ownId ? "font-bold" : ""}`}>
      <td className="p-2">{i + 1}</td>
      <td className="p-2">
        <span className="inline-flex items-center gap-2">
          {row.sponsor && row.sponsorPlacement && row.sponsorPlacement !== "board" ? (
            <SponsorMark brand={row.sponsor} size={22} />
          ) : null}
          <span>
            {label}
            {row.isAi ? " (AI)" : ""}
          </span>
        </span>
      </td>
      <td className="p-2">{row.played}</td>
      <td className="p-2">{row.wins}</td>
      <td className="p-2">{row.draws}</td>
      <td className="p-2">{row.losses}</td>
      {tab === "regular" ? (
        <td className="p-2">
          {row.scoredFor}:{row.scoredAgainst}
        </td>
      ) : null}
      {tab === "regular" ? <td className="p-2">{row.diff}</td> : null}
      {tab === "flowliga" ? <td className="p-2">{row.restFor}</td> : null}
      {tab === "flowliga" ? <td className="p-2">{row.restAgainst}</td> : null}
      <td className="p-2">{row.points}</td>
    </tr>
  );
}

const ManagerLeague = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("regular");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    if (!userId) return;
    vw.league(userId)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  };

  useEffect(load, [userId]);

  const comps = manager?.competitions || manager?.progression?.manager?.competitions || {};
  const guide = manager?.guide || manager?.progression?.manager?.guide;
  const leagueStarted = Boolean(comps.leagueStarted);
  const worldReady = Number(comps.worldReady || 0);
  const worldNeeded = Number(comps.worldNeeded || 8);
  const awaitingFirstLeague =
    Boolean(manager?.guided) &&
    leagueStarted &&
    (guide?.current?.key === "league_play" ||
      (Boolean(manager?.club?.firstTrainingDoneAt) && Number(manager?.club?.played || 0) < 1) ||
      searchParams.get("next") === "match");

  const visibleTabs = useMemo(
    () =>
      TABS.filter((item) => {
        if (item.id === "regular") return true;
        if (awaitingFirstLeague) return false;
        if (item.id === "flowliga") return Boolean(comps.flowliga);
        if (item.id === "tournament") return Boolean(comps.tournament);
        return true;
      }),
    [comps.flowliga, comps.tournament, awaitingFirstLeague]
  );

  useEffect(() => {
    const wanted = searchParams.get("tab");
    if (wanted && visibleTabs.some((t) => t.id === wanted)) {
      setTab(wanted);
      return;
    }
    if (!visibleTabs.some((t) => t.id === tab) && visibleTabs[0]) {
      setTab(visibleTabs[0].id);
    }
  }, [tab, visibleTabs, searchParams]);

  if (loading) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;
  if (!data) return <Loading />;

  const enter = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.enterTournament(userId);
      setData(res.data);
      await refresh();
    } catch (err) {
      setMsg(err?.data?.message || "Startgeld konnte nicht gezahlt werden.");
    } finally {
      setBusy(false);
    }
  };

  const block = tab === "flowliga" ? data.flowliga : tab === "tournament" ? data.tournament : data.regular;
  const meta = data.competitions?.[tab];
  const ownId = manager.club.id;
  const championName = data.tournament?.bracket?.championName;
  const day = data.managerDay || {};

  // Begleitung: nur Tabelle + klarer Weg zum Ligaspiel
  if (awaitingFirstLeague) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 py-2">
        <div className="rounded-3xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-5">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Kurz ansehen — dann spielen</div>
          <h2 className="text-2xl font-black mb-2">Deine Divisionstabelle</h2>
          <p className="opacity-80 mb-4">
            Hier siehst du, wo {manager.club.name} startet. Als Nächstes bestreitest du dein erstes Ligaspiel.
          </p>
          <Link
            to="/manager/match"
            className="inline-flex px-6 py-3 rounded-xl bg-emerald-700 text-white font-bold text-lg"
          >
            Zum Ligaspiel
          </Link>
        </div>
        <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden">
          <h2 className="text-xl font-bold p-4">📊 Tabelle</h2>
          <table className="w-full text-left">
            <TableHead tab="regular" />
            <tbody>
              {(data.regular?.table || []).map((row, i) => (
                <TableRow key={row.id} row={row} i={i} tab="regular" ownId={ownId} />
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm opacity-60">
          Spielplan und Manager-Day kommen nach dem ersten Ligaspiel dazu.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {!leagueStarted ? (
        <div className="rounded-3xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
          <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">Ligastatus</div>
          <h2 className="text-2xl font-black mb-2">
            {worldReady}/{worldNeeded} Vereine — Liga noch nicht gestartet
          </h2>
          <p className="opacity-80 mb-4">
            Die Divisionstabelle und Ligaspiele starten, sobald genug Clubs da sind. Bis dahin Freundschaftsspiele und
            Freunde einladen.
          </p>
          <Link to="/manager/match" className="inline-flex px-5 py-3 rounded-xl bg-emerald-700 text-white font-semibold mr-3">
            Freundschaftsspiel
          </Link>
          <Link to="/manager" className="inline-flex px-5 py-3 rounded-xl bg-white dark:bg-gray-700 font-semibold border">
            Zurück zum Club
          </Link>
        </div>
      ) : null}
      <p>
        Saison {data.seasonNumber} · Spieltag {data.matchday} · {data.status}
      </p>
      <p className="text-sm opacity-80">
        Freigeschaltete Wettbewerbe deines Clubs. FlowLiga und Turniere erscheinen, sobald du die Grundlagen gespielt
        hast.
      </p>
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <div className="text-xs uppercase tracking-wide opacity-70">Anmelde-Center</div>
        <div className="font-black text-xl">
          {comps.flowliga ? "Reguläre Liga und Team-FlowLiga: angemeldet" : "Reguläre Liga: angemeldet"}
        </div>
        <p className="text-sm opacity-80">
          {comps.tournament
            ? "Das Team-Turnier ist optional: Du trittst erst nach Zahlung des Startgelds in den K.-o.-Baum ein."
            : "Team-Turnier und FlowLiga werden freigeschaltet nach 2 Ligaspielen oder dem ersten Ligasieg."}
        </p>
      </div>
      <div className="rounded-2xl border border-yellow-400 bg-gray-900 text-white p-4">
        <div className="text-xs uppercase tracking-wide text-yellow-200">Manager-Day</div>
        <div className="font-black text-xl">
          Weiter sobald alle fertig · sonst Auto-Sim {dt(day.autoSimAt || day.lineupLockAt)}
          {day.autoSimAt || day.lineupLockAt
            ? ` (${relativeUntil(day.autoSimAt || day.lineupLockAt)})`
            : ""}
        </div>
        <p className="text-sm opacity-80">
          KI-Spiele laufen mit, sobald jemand gespielt hat. Echte Manager spielen selbst — der Spieltag dreht
          sofort weiter, wenn Liga und FlowLiga durch sind. Nur wer nicht reagiert, wird um 0:00 mitgenommen.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {visibleTabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`px-4 py-2 rounded-md text-sm font-semibold border ${
              tab === item.id
                ? "bg-gray-900 text-white border-yellow-400"
                : "bg-white dark:bg-gray-700 border-gray-300"
            }`}
          >
            {item.emoji ? <span className="mr-1">{item.emoji}</span> : null}
            {item.label}
          </button>
        ))}
      </div>
      {meta?.blurb ? <p className="text-sm opacity-80">{meta.blurb}</p> : null}
      {msg ? <p className="text-red-600">{msg}</p> : null}

      {tab === "tournament" ? (
        <div className="rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm">
          {data.tournament?.entryOpen ? (
            <p>
              Anmeldung offen
              {data.tournament.fieldSize
                ? ` · Feld ${data.tournament.entrants || 0}/${data.tournament.fieldSize}`
                : ""}
              {data.tournament.entryClosesAt ? (
                <>
                  {" "}
                  · spätestens bis <strong>{dt(data.tournament.entryClosesAt)}</strong>
                  {data.tournament.entryClosesRelative
                    ? ` (${data.tournament.entryClosesRelative})`
                    : ` (${relativeUntil(data.tournament.entryClosesAt)})`}
                </>
              ) : null}
              . Bei vollem Feld startet der K.-o.-Baum sofort — keine Nachmeldung.
            </p>
          ) : data.tournament?.status === "running" ? (
            <p>
              Turnier läuft
              {data.tournament.startedLabel || data.tournament.startedAt
                ? ` seit ${data.tournament.startedLabel || dt(data.tournament.startedAt)}`
                : ""}
              . Anmeldung geschlossen.
            </p>
          ) : (
            <p>Anmeldung für dieses Turnier ist geschlossen.</p>
          )}
        </div>
      ) : null}

      {tab === "tournament" && !data.tournament?.entered ? (
        <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
          <p className="mb-3">
            Startgeld {money(data.tournament?.entryFee)}. Danach K.-o. bis zum Finale. Checkout-Summe entscheidet jede
            Runde. Plätze 1–3 teilen den Lostopf.
          </p>
          <button
            disabled={busy || data.tournament?.entryOpen === false}
            onClick={enter}
            className="px-4 py-2 bg-green-500 text-white border border-yellow-400 rounded font-semibold disabled:opacity-50"
          >
            {data.tournament?.entryOpen === false
              ? "Anmeldung geschlossen"
              : "Startgeld zahlen und antreten"}
          </button>
        </div>
      ) : null}

      {tab === "tournament" && data.tournament?.entered ? (
        <p className="text-sm">
          Angemeldet · Status {data.tournament.status}
          {data.tournament.entryOpen
            ? ` · wartet auf volles Feld${
                data.tournament.fieldSize
                  ? ` (${data.tournament.entrants || 0}/${data.tournament.fieldSize})`
                  : ""
              }`
            : ""}
        </p>
      ) : null}

      {tab === "tournament" ? (
        <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
          <h2 className="text-xl font-bold mb-3">🌳 Turnierbaum</h2>
          <p className="text-sm opacity-70 mb-4">
            Keine Tabelle und kein Diff. Wer in der Runde mehr Checkout-Summe hat, kommt weiter. Dein Club ist grün
            umrandet.
          </p>
          <TournamentBracket bracket={data.tournament?.bracket} championName={championName} />
          <p className="text-sm mt-4">
            Nächste Runde spielen unter{" "}
            <Link className="underline" to="/manager/match">
              Wettbewerbe
            </Link>
            .
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden">
          <h2 className="text-xl font-bold p-4">📊 Tabelle</h2>
          {tab === "flowliga" ? (
            <p className="px-4 pb-2 text-sm opacity-70">
              Match: Wer checkt, bekommt die Restpunkte des Gegners (Flow-Punkte — mehr gewinnt). Tabelle: „Rest“ =
              eigene übrig gebliebene Last über die Saison (weniger besser), „Gegner-Rest“ = was du den anderen
              abgenommen hast.
            </p>
          ) : (
            <p className="px-4 pb-2 text-sm opacity-70">
              Spiele sind gewonnene gegen verlorene Begegnungen (16 Einzel + 2 Doppel). Diff ist die Differenz daraus.
            </p>
          )}
          <table className="w-full text-left">
            <TableHead tab={tab} />
            <tbody>
              {(block?.table || []).map((row, i) => (
                <TableRow key={row.id} row={row} i={i} tab={tab} ownId={ownId} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab !== "tournament" ? (
        <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
          <h2 className="text-xl font-bold mb-3">📅 Spielplan</h2>
          <ul className="space-y-1 text-sm">
            {(block?.fixtures || []).map((f) => (
              <li key={f.id}>
                Spieltag {f.matchday}: {f.home} vs {f.away} — {f.score}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
        <h2 className="text-xl font-bold mb-3">⭐ Beste Spieler (virtuell)</h2>
        <ul>
          {(block?.stats || []).map((s) => (
            <li key={s.playerKey}>
              {s.playerKey} · {s.wins} Siege · AVG {s.avg}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ManagerLeague;
